import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { BrandMark } from './BrandMark'

// A load that answers inside this never shows the mark, so a fast boot does
// not flash a logo on and off.
const SHOW_AFTER_MS = 300

// Loaders on screen right now, and when the last one left. The boot loader
// and the Focus loader that follows it are separate mounts (AuthGate hands
// over to the app), so without this the mark would vanish for another 300 ms
// in the middle of one continuous wait.
let onScreen = 0
let lastLeftAt = -Infinity

function useShownAfterDelay(): { shown: boolean; handedOver: boolean } {
  const [handedOver] = useState(() => onScreen > 0 || performance.now() - lastLeftAt < 100)
  const [shown, setShown] = useState(handedOver)

  useEffect(() => {
    if (shown) return
    const t = setTimeout(() => setShown(true), SHOW_AFTER_MS)
    return () => clearTimeout(t)
  }, [shown])

  useEffect(() => {
    if (!shown) return
    onScreen++
    return () => {
      onScreen--
      lastLeftAt = performance.now()
    }
  }, [shown])

  return { shown, handedOver }
}

/**
 * The animated brand mark, centred in whatever area is waiting for its first
 * data: the app booting, a view's cold load, a lead or an admin tab on its
 * way. A refresh keeps the data already on screen instead.
 */
export function BrandLoader({ label = 'Laddar', className }: {
  label?: string
  className?: string
}) {
  const { shown, handedOver } = useShownAfterDelay()
  return (
    <div role="status" aria-label={label} className={cn('flex items-center justify-center', className)}>
      {shown && (
        <span aria-hidden="true" className={handedOver ? undefined : 'animate-[fadeIn_var(--dur-slow)_ease-out]'}>
          <BrandMark animated />
        </span>
      )}
    </div>
  )
}
